/**
 * OpenAI-compatible chat against a connected catalog / Forge endpoint.
 */

import type { GpuInstance } from "./types";

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string | ContentPart[];
}

export type ContentPart =
  | { type: "text"; text: string }
  | { type: "image_url"; image_url: { url: string } };

export type InferenceSource =
  | "hf"
  | "cloudflare"
  | "civitai"
  | "forge_endpoint"
  | "custom";

export interface InferenceTarget {
  baseUrl: string;
  apiKey: string;
  model: string;
  source: InferenceSource;
  label: string;
  instanceId?: string;
  bindKind?: string;
}

const CHAT_OK_STATUS = new Set(["ready", "running", "loading"]);

const CATALOG_SOURCES = new Set<InferenceSource>([
  "hf",
  "cloudflare",
  "civitai",
  "forge_endpoint",
]);

export function isCatalogSource(s: string | null | undefined): s is InferenceSource {
  return s === "hf" || s === "cloudflare" || s === "civitai" || s === "forge_endpoint";
}

export function isBilledSource(s: string | undefined): boolean {
  return isCatalogSource(s);
}

export function isInferenceReady(inst: GpuInstance | null | undefined): boolean {
  return Boolean(
    inst &&
      CHAT_OK_STATUS.has(inst.status) &&
      inst.billingActive !== false &&
      (inst.endpointUrl || "").trim(),
  );
}

export function needsEndpoint(inst: GpuInstance | null | undefined): boolean {
  return Boolean(
    inst &&
      CHAT_OK_STATUS.has(inst.status) &&
      inst.billingActive !== false &&
      !(inst.endpointUrl || "").trim(),
  );
}

export function resolveInferenceTarget(
  instance: GpuInstance | null,
  opts?: {
    customBaseUrl?: string;
    customApiKey?: string;
    customModel?: string;
    useCustom?: boolean;
    source?: InferenceSource;
    label?: string;
  },
): InferenceTarget | null {
  if (opts?.useCustom && opts.customBaseUrl?.trim()) {
    const source: InferenceSource = isCatalogSource(opts.source) ? opts.source : "custom";
    return {
      baseUrl: normalizeBase(opts.customBaseUrl),
      apiKey: opts.customApiKey || "",
      model: opts.customModel || "custom",
      source,
      label: opts.label || "Custom endpoint",
    };
  }

  if (!isInferenceReady(instance) || !instance?.endpointUrl) return null;

  const model =
    instance.activeModelRepoId ||
    instance.modelRepoId ||
    instance.modelName ||
    "default";

  const source: InferenceSource = isCatalogSource(opts?.source)
    ? opts!.source!
    : "custom";

  return {
    baseUrl: normalizeBase(instance.endpointUrl),
    apiKey: instance.sessionToken || "",
    model,
    source,
    label: opts?.label || `${instance.modelName || model}`,
    instanceId: instance.id,
  };
}

function normalizeBase(url: string): string {
  let u = (url || "").trim().replace(/\/$/, "");
  u = u.replace(/\/chat\/completions\/?$/i, "");
  return u;
}

export function chatCompletionsUrl(baseUrl: string): string {
  const base = normalizeBase(baseUrl);
  if (/\/v1$/i.test(base)) return `${base}/chat/completions`;
  if (/\/v1\//i.test(base)) return `${base.replace(/\/$/, "")}/chat/completions`;
  return `${base}/v1/chat/completions`;
}

function extractNonStream(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const d = data as Record<string, unknown>;
  const choices = d.choices;
  if (Array.isArray(choices) && choices[0]) {
    const c0 = choices[0] as Record<string, unknown>;
    const msg = c0.message as Record<string, unknown> | undefined;
    if (typeof msg?.content === "string") return msg.content;
    if (Array.isArray(msg?.content)) {
      return (msg!.content as unknown[])
        .map((p) =>
          typeof p === "string"
            ? p
            : p && typeof p === "object" && typeof (p as { text?: string }).text === "string"
              ? (p as { text: string }).text
              : "",
        )
        .join("");
    }
    if (typeof c0.text === "string") return c0.text;
  }
  if (typeof d.output_text === "string") return d.output_text;
  return "";
}

export async function chatCompletion(
  target: InferenceTarget,
  messages: ChatMessage[],
  opts?: {
    temperature?: number;
    maxTokens?: number;
    signal?: AbortSignal;
  },
): Promise<string> {
  const url = chatCompletionsUrl(target.baseUrl);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (target.apiKey?.trim()) {
    headers.Authorization = `Bearer ${target.apiKey.trim()}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify({
      model: target.model || "default",
      messages,
      temperature: opts?.temperature ?? 0.2,
      max_tokens: opts?.maxTokens ?? 800,
      stream: false,
    }),
    signal: opts?.signal ?? AbortSignal.timeout(60000),
  });

  if (!res.ok) {
    const t = await res.text().catch(() => "");
    if (res.status === 402 || res.status === 403) {
      throw new Error(t || "Insufficient suite credits or access denied.");
    }
    throw new Error(t.slice(0, 240) || `Brain HTTP ${res.status}`);
  }

  const data = await res.json();
  const text = extractNonStream(data);
  if (!text) throw new Error("Empty brain response");
  return text;
}

export { CATALOG_SOURCES };
